import React from "react";
import { Link } from "react-router-dom";
import {
  FaPlaneArrival,
  FaClock,
  FaCarSide,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaPlaneDeparture,
  FaShieldAlt,
  FaArrowRight,
} from "react-icons/fa";
// Ensure you have an appropriate image or use a solid background fallback
import heroImage from "../../assets/react-cars-hero-bg.jpg";

const topAirports = [
  { code: "LAX", name: "Los Angeles Int'l", city: "Los Angeles, CA" },
  { code: "MCO", name: "Orlando Int'l", city: "Orlando, FL" },
  { code: "LAS", name: "Harry Reid Int'l", city: "Las Vegas, NV" },
  { code: "JFK", name: "John F. Kennedy", city: "New York, NY" },
  { code: "ATL", name: "Hartsfield-Jackson", city: "Atlanta, GA" },
  { code: "DEN", name: "Denver Int'l", city: "Denver, CO" },
];

const faqs = [
  {
    q: "Do I need to take a shuttle to get my rental car?",
    a: "It depends on the airport. Many major airports have rental counters directly in the terminal with cars parked in an adjacent garage. Others have a consolidated rental car facility (CONRAC) accessible via a quick, free airport transit train or shuttle.",
  },
  {
    q: "What happens if my flight is delayed?",
    a: "When you book, you'll be asked to provide your flight number. Rental agencies monitor flight statuses and will typically hold your reservation if your flight is delayed.",
  },
  {
    q: "Are airport car rentals more expensive?",
    a: "Airport locations sometimes include specific airport concession fees or taxes. However, the unmatched convenience of driving away immediately upon landing often outweighs these nominal fees.",
  },
];

export default function AirportCarRental() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* HERO SECTION */}
      <section className="relative flex min-h-[85vh] items-center justify-center overflow-hidden bg-slate-950 px-4 py-20 sm:px-6">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Airport Car Rental"
            className="h-full w-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/60 to-slate-950" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <div className="mb-6 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-black uppercase tracking-[0.2em] text-white backdrop-blur-md">
              <FaPlaneArrival className="text-blue-400" /> Premium Airport
              Service
            </span>
          </div>
          <h1 className="mb-6 text-5xl font-black leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
            Skip the Line. <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-200">
              Drive Away.
            </span>
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-lg font-medium leading-relaxed text-slate-300 sm:text-xl">
            Book terminal-adjacent and on-airport car rentals. Step off your
            flight, grab your keys, and get your trip started without waiting
            for off-site shuttles.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/cars"
              className="group flex w-full items-center justify-center gap-3 rounded-full bg-[#2871d3] px-8 py-4 text-base font-black text-white shadow-xl shadow-blue-600/20 transition-all duration-300 hover:-translate-y-1 hover:bg-[#1d5ab3] hover:shadow-blue-600/30 sm:w-auto"
            >
              Search Airport Rentals
              <FaArrowRight className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST BANNER */}
      <section className="border-b border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-8 px-4 sm:gap-16 sm:px-6">
          {[
            { icon: <FaMapMarkerAlt />, text: "300+ US Airports" },
            { icon: <FaShieldAlt />, text: "No Hidden Fees" },
            { icon: <FaPlaneDeparture />, text: "Flight Monitoring" },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 text-slate-600">
              <div className="text-xl text-[#2871d3]">{item.icon}</div>
              <span className="text-sm font-bold uppercase tracking-wide">
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* VALUE PROPOSITION GRID */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-24">
        <div className="mb-16 text-center">
          <h2 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
            Why book an Airport Rental?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base font-medium text-slate-500">
            Maximize your vacation time and minimize transit stress with our
            on-airport rental partners.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {[
            {
              icon: <FaPlaneArrival />,
              title: "Terminal Convenience",
              desc: "Pick up your vehicle steps away from baggage claim at major US airports. No dragging luggage onto crowded buses.",
            },
            {
              icon: <FaClock />,
              title: "Save Precious Time",
              desc: "Avoid off-airport shuttle buses and long transit times to remote lots. Start your vacation the minute you land.",
            },
            {
              icon: <FaCarSide />,
              title: "Largest Fleets",
              desc: "Airport locations stock the widest variety of vehicle classes and sizes, meaning you get exactly what you booked.",
            },
          ].map((feature, idx) => (
            <div
              key={idx}
              className="group rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-[0_20px_40px_rgb(40,113,211,0.08)]"
            >
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-3xl text-[#2871d3] transition-transform duration-300 group-hover:scale-110 group-hover:bg-[#2871d3] group-hover:text-white">
                {feature.icon}
              </div>
              <h3 className="mb-3 text-xl font-black text-slate-900">
                {feature.title}
              </h3>
              <p className="text-sm font-medium leading-relaxed text-slate-500">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#0f172a] py-20 text-white sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-16 text-center">
            <h2 className="text-3xl font-black tracking-tight sm:text-4xl">
              Seamless from Touchdown to Highway
            </h2>
          </div>

          <div className="grid gap-12 md:grid-cols-3 md:gap-8">
            {[
              {
                step: "01",
                title: "Book in Advance",
                desc: "Enter your arrival airport and flight details. Secure your rate before landing.",
              },
              {
                step: "02",
                title: "Land & Locate",
                desc: "Follow the 'Rental Cars' signs from baggage claim directly to the agency counter.",
              },
              {
                step: "03",
                title: "Grab the Keys",
                desc: "Show your voucher and license, get your keys, and hit the road immediately.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="relative flex flex-col items-center text-center"
              >
                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border-4 border-slate-800 bg-slate-900 text-2xl font-black text-[#2871d3] shadow-[0_0_30px_rgb(40,113,211,0.2)]">
                  {item.step}
                </div>
                <h3 className="mb-3 text-xl font-bold">{item.title}</h3>
                <p className="text-slate-400">{item.desc}</p>
                {/* Connector line for desktop */}
                {idx !== 2 && (
                  <div className="absolute left-[60%] top-10 hidden h-0.5 w-[80%] bg-slate-800 md:block"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR AIRPORTS */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-24">
        <h2 className="mb-10 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
          Top US Airport Locations
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {topAirports.map((airport) => (
            <Link
              key={airport.code}
              to={`/cars?pickup=${airport.code}`}
              className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-blue-200 hover:bg-blue-50"
            >
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 font-mono text-sm font-black text-slate-600 transition-colors group-hover:bg-[#2871d3] group-hover:text-white">
                  {airport.code}
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">{airport.name}</h3>
                  <p className="text-xs font-medium text-slate-500">
                    {airport.city}
                  </p>
                </div>
              </div>
              <FaArrowRight className="text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-[#2871d3]" />
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <h2 className="mb-10 text-center text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <details
                key={idx}
                className="group rounded-2xl border border-slate-200 bg-[#f8f9fc] p-6 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-slate-900">
                  {faq.q}
                  <span className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-blue-600 shadow-sm transition-transform group-open:rotate-180">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </summary>
                <p className="mt-4 text-sm font-medium leading-relaxed text-slate-600">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="bg-[#2871d3] py-16 text-center text-white sm:py-20">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="mb-6 text-3xl font-black sm:text-4xl">
            Ready to hit the road?
          </h2>
          <p className="mb-8 text-lg font-medium text-blue-100">
            Search live availability across all major US airports and lock in
            your vehicle today.
          </p>
          <Link
            to="/cars"
            className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-black text-[#2871d3] shadow-lg transition-transform hover:-translate-y-1 hover:shadow-xl active:translate-y-0"
          >
            Start Your Search
            <FaArrowRight />
          </Link>
        </div>
      </section>
    </div>
  );
}
